import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Tooltip, Divider, LinearProgress } from '@material-ui/core';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useHotkeys } from 'react-hotkeys-hook';
import { enqueueSnackbar } from 'notistack';
import lodash from 'lodash';
import cx from 'classnames';

import { bytesToMB, findSearchValue, getVideoResolution } from '../../../../helpers';
import { ContainedButton, OutlinedButton } from '../../../../components/modules/Buttons';
import { refetchFeeds } from '../../../../store/videoFeedsList/videoFeedsListSlice';
import { ReactComponent as Minimize } from '../../../../assets/icons/minimize.svg';
import CancelProcessModal from '../../../../components/modules/CancelProcessModal';
import { ReactComponent as Close } from '../../../../assets/icons/close.svg';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  deleteVideofeed,
  getUserOrganization,
  postCancelMergeSession,
  postCreateVideoFeed,
  updateVideofeedTitle,
} from '../../../../api';
import TrashButton from '../../../../components/modules/TrashButton';
import ProgressBar from '../../../../components/modules/ProgressBar';
import FileUpload from '../../../../components/modules/FileUpload';
import { UPLOAD_TYPE, VIDEOFEED } from '../../../../enums';
import Modal from '../../../../components/modules/Modal';
import {
  UploadContentState,
  UploadingStatus,
  clearUploadingFiles,
  updateArrayOfCurrentUploads,
  updateFolderIdToUpload,
  updateUploadProgress,
  updateUploadingContentState,
} from '../../../../store/sharedStore/sharedStoreSlice';
import styles from './SidebarModals.module.scss';
import AwsFile from './AwsFile';
import { saveMyOrganization } from '../../../../store/user/userSlice';
import FullPageLoader from '../../../../components/modules/FullPageLoader';
import { abortActiveAwsUploads } from '../../../../helpers/useAwsUploader';

type TUploadContentModal = {
  open: boolean;
  setOpen: (bool: boolean) => void;
  setMinimize: (bool: boolean) => void;
  allowNewUpload: boolean;
  showMinimizedModal: boolean;
  disabled: boolean;
};

export type TLocalPackage = {
  name: string;
  size: number;
  uploadedBytes?: number;
  progress: number;
  movFiles: File[];
  movMeta?: { quality: number; duration?: number }[];
  zipContent: Blob;
  upload: boolean;
  singleMov?: boolean;
  originalName: string;
  multi_quality: boolean;
  overallProgress?: string;
  localStorageKey?: string;
  fileListNames: string[];
  disableQuality?: boolean;
  title?: string;
  uploadMode?: UploadMode;
  uploadError?: string;
  uploadTrigger?: number;
};

export type UploadMode = 'package' | 'append' | 'separate';

export interface ExtendedVIDEOFEED extends VIDEOFEED {
  originalName: string;
  uploadedBytes: number;
  size: number;
  overallProgress: number;
  metadataUploaded?: boolean;
  multi_quality?: boolean;
  fileListNames: string[];
  uploadMode?: UploadMode;
  mergeSessionId?: string;
}

interface NewFileNames extends TLocalPackage {
  newName: string;
}

// ---- Module-scope in-memory de-dupe for create (parent-owned) ----
const __vfCreateCache = new Map<string, Promise<any>>();
const createOnce = (key: string, fn: () => Promise<any>) => {
  if (__vfCreateCache.has(key)) return __vfCreateCache.get(key)!;
  const p = (async () => {
    return await fn();
  })();
  __vfCreateCache.set(key, p);
  void p.then((result) => {
    if (!result) {
      __vfCreateCache.delete(key);
    }
  }).catch(() => __vfCreateCache.delete(key));
  return p;
};

const stableKey = (args: { folderId: string; size: number; originalName: string }) =>
  `=videofeedToResume=${args.folderId}::${args.size}::${args.originalName}`;

const showPersistentUploadWarning = (message: string) => {
  enqueueSnackbar(message, {
    preventDuplicate: true,
    variant: 'warning',
    persist: true,
    anchorOrigin: { horizontal: 'right', vertical: 'top' },
  });
};

const isAdditionalAngleUpload = (item?: UploadContentState | null) => item?.uploadMode === 'additional-angle';
const getAdditionalAngleUploads = (items: UploadContentState[]) => items.filter(isAdditionalAngleUpload);
const getUploadContentUploads = (items: UploadContentState[]) => items.filter((item) => item && !isAdditionalAngleUpload(item));
const mergeUploadContentUploads = (uploadContentItems: UploadContentState[], currentItems: UploadContentState[]) => [
  ...getUploadContentUploads(uploadContentItems),
  ...getAdditionalAngleUploads(currentItems),
];

const getStoredResumeUpload = (key: string) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as ExtendedVIDEOFEED) : null;
  } catch {
    return null;
  }
};

const uploadMatchesPackage = (upload: UploadContentState, pkg: TLocalPackage) => {
  const uploadOriginalName = upload?.originalName || (upload as any)?.original_name;
  const uploadSize = Number((upload as any)?.size);
  const sizeMatches = !Number.isFinite(uploadSize) || uploadSize === Number(pkg.size);
  const videosMatch = !upload?.videos?.length || upload.videos.length === pkg.movFiles.length;
  return uploadOriginalName === pkg.originalName && sizeMatches && videosMatch;
};

const UploadContentModal: React.FC<TUploadContentModal> = ({
  open,
  setOpen,
  setMinimize,
  allowNewUpload,
  showMinimizedModal,
  disabled,
}) => {
  const [packageList, setPackageList] = useState<TLocalPackage[]>([]);
  const doneButtonRef = useRef<HTMLDivElement>(null);
  const uploadButtonRef = useRef<HTMLDivElement>(null);
  const packageListContainerRef = useRef<HTMLDivElement>(null);
  const prevPackageCountRef = useRef(0);

  // if u want to enable reorder uploads logic, just renamed skiprearrangeDisabled to rearrangeDisabled (and delete "const rearrangeDisabled = true");
  const [rearrangeDisabled, setRearrangeDisabled] = useState(false);
  // const rearrangeDisabled = true;
  const [showFileInput, setShowFileInput] = useState(true);
  const [uploadEnabled, setUploadEnabled] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const packageListRef = useRef(packageList);
  const uploadingRef = useRef(uploading);
  const loadingRef = useRef(loading);
  const preparingUploadsRef = useRef(false);

  const { transcodingStatus, uploadingContentState, uploadingFiles, arrayOfCurrentUploads, commonTrigger, folderIdToUpload, pkgToDelete } =
    useAppSelector((state) => state.sharedStore);
  const { refetchFeedList } = useAppSelector((state) => state.videoFeedsListStore);
  const { myOrganization } = useAppSelector((state) => state.user);

  const [disableDropMore, setDisableDropMore] = useState(false);

  useEffect(() => {
    if (disableDropMore && myOrganization?.allowedVideoQualities) {
      notification.showMessage('Please select the quality before dropping the package into the upload modal.');
    }
  }, [disableDropMore]);

  useEffect(() => {
    if (open) {
      const uploadContentUploads = getUploadContentUploads(uploadingContentState);
      packageListRef.current = packageList;
      uploadingContentRef.current = uploadingContentState;
      setTotalSize(packageList.reduce((a, b) => (a += b.size), 0));
      if (!packageList.length || !folderIdToUpload || !uploadContentUploads.length) {
        dispatch(updateArrayOfCurrentUploads([]));
        return;
      }

      const newArrOfCurrentUploads = packageList.reduce<{ videofeedName: string; uploadedBytes: number }[]>((acc, pkg, index) => {
        const uploadState =
          uploadContentUploads.find(
            (state) =>
              state &&
              state.originalName === pkg.originalName &&
              Number(state.size) === Number(pkg.size) &&
              (state.uploadMode || 'package') === (pkg.uploadMode || 'package'),
          ) || uploadContentUploads[index];
        if (!uploadState) {
          return acc;
        }

        if (uploadState.status === UploadingStatus.UPLOADING || uploadState.status === UploadingStatus.ADDED) {
          const parsedPkg = getStoredResumeUpload(
            stableKey({
              folderId: folderIdToUpload,
              size: pkg.size,
              originalName: pkg.originalName,
            }),
          );
          if (parsedPkg && isFinite(+parsedPkg.uploadedBytes)) {
            acc.push({ videofeedName: pkg.originalName, uploadedBytes: +parsedPkg.uploadedBytes });
          }
        } else if (uploadState.status === UploadingStatus.TRANSCODING || uploadState.status === UploadingStatus.FINISHED) {
          acc.push({ videofeedName: pkg.originalName, uploadedBytes: uploadState.videos?.reduce((a, b) => (a += +b.size), 0) ?? 0 });
        }

        return acc;
      }, []);

      dispatch(updateArrayOfCurrentUploads(newArrOfCurrentUploads));
    }
  }, [packageList, uploadingContentState, uploadingFiles, commonTrigger, open, folderIdToUpload]);

  const [uploadedAll, setUploadedAll] = useState(false);
  const [totalSize, setTotalSize] = useState(0);

  const uploadingContentRef = useRef(uploadingContentState);

  useEffect(() => {
    uploadingContentRef.current = uploadingContentState;
  }, [uploadingContentState]);

  const dispatch = useAppDispatch();
  const notification = useSnackbar();

  useEffect(() => {
    uploadingRef.current = uploading;
    loadingRef.current = loading;
  }, [uploading, loading]);

  useEffect(() => {
    if (!open) {
      prevPackageCountRef.current = packageList.length;
      return;
    }
    if (packageList.length > prevPackageCountRef.current) {
      requestAnimationFrame(() => {
        const listEl = packageListContainerRef.current;
        if (listEl) {
          listEl.scrollTop = listEl.scrollHeight;
        }
      });
    }
    prevPackageCountRef.current = packageList.length;
  }, [packageList.length, open]);

  useEffect(() => {
    if (!allowNewUpload && transcodingStatus) {
      dispatch(updateUploadProgress(0));
    }
  }, [transcodingStatus]);

  const [showCancelUploadModal, setShowCancelUploadModal] = useState(false);
  const [stopAndDeleteLoading, setStopAndDeleteLoading] = useState(false);
  const switchCancelUploadModal = () => {
    setShowCancelUploadModal(false);
  };

  const switchModal = () => {
    if (uploadingRef.current || loadingRef.current) {
      setShowCancelUploadModal(true);
    } else {
      __vfCreateCache.clear();
      setPackageList([]);
      dispatch(updateArrayOfCurrentUploads([]));
      setOpen(false);
      setUploading(false);
      setLoading(false);
      setMinimize(false);
      setUploadedAll(false);
      setCancelUpload(false);
      setShowFileInput(true);
      setUploadEnabled(true);
      dispatch(updateUploadingContentState(getAdditionalAngleUploads(uploadingContentRef.current)));
      dispatch(clearUploadingFiles());
      setNewFileNames([]);
      setUploadComplete(false);
    }
  };

  const onSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    preparingUploadsRef.current = true;
    const folderId = findSearchValue('folderId') as string;
    setRearrangeDisabled(true);
    dispatch(updateFolderIdToUpload(folderId));
    e?.preventDefault();

    let sizeOfAllFiles = 0;
    packageList.forEach((f) => {
      sizeOfAllFiles += f.size;
    });
    setLoading(true);
    setUploading(true);

    const uploadContentArr = [];
    const nextPackageList = lodash.cloneDeep(packageList);

    for (let f = 0; f < packageList.length; f++) {
      const currentPackage = nextPackageList[f];
      sizeOfAllFiles += currentPackage.size;
      const keyStorage = stableKey({
        folderId,
        size: currentPackage.size,
        originalName: currentPackage.originalName,
      });
      const json = localStorage.getItem(keyStorage);
      let availableToUpload: any;
      let itsSingleVideo = false;
      let uploadType: UPLOAD_TYPE.PACKAGE | UPLOAD_TYPE.STANDALONE = UPLOAD_TYPE.PACKAGE;

      if (currentPackage.uploadMode === 'append') {
        availableToUpload = null;
      } else if (json) {
        availableToUpload = { data: JSON.parse(json) };
        nextPackageList[f] = { ...nextPackageList[f], uploadError: '' };
      } else {
        const videoSizeAndIdArray = currentPackage.movFiles.map((v, i) => {
          let path =
            v.webkitRelativePath.length > 0
              ? v.webkitRelativePath?.split('/')[3]
              : // @ts-ignore
                v.customPath?.split('/')[3];

          // @ts-ignore
          if (v.webkitRelativePath.includes('Single Videos Local') || v.customPath.includes('Single Videos Local')) {
            // @ts-ignore
            const match = v.webkitRelativePath.match(/Single Videos Local\/Video [^\/]+/);
            // @ts-ignore
            const matchCustomPath = v.customPath.match(/Single Videos Local\/Video [^\/]+/);
            if (match?.length) {
              const id = match[0].split('/')[1];
              if (id) {
                path = id;
              }
            } else if (matchCustomPath?.length) {
              const id = matchCustomPath[0].split('/')[1];
              if (id) {
                path = id;
              }
            }
          }
          if (!path) {
            itsSingleVideo = true;
            path = `${currentPackage.name}_video_single_${i}`;
            uploadType = UPLOAD_TYPE.STANDALONE;
          } else {
            uploadType = UPLOAD_TYPE.PACKAGE;
          }
          if (uploadType === UPLOAD_TYPE.PACKAGE) {
            return {
              video_id: path,
              size: v.size,
            };
          } else {
            return {
              video_id: path,
              size: v.size,
              angle_name: v.name.slice(0, -4),
            };
          }
        });
        for (let i = 0; i < videoSizeAndIdArray.length; i++) {
          const { height: quality, duration } = await getVideoResolution(currentPackage.movFiles[i]);
          // @ts-ignore
          videoSizeAndIdArray[i].quality = quality;
          if (duration) {
            // @ts-ignore
            videoSizeAndIdArray[i].duration = duration;
          }
        }

        const movMeta = videoSizeAndIdArray.map((v: any) => ({
          quality: Number(v.quality) || 0,
          duration: typeof v.duration === 'number' ? v.duration : undefined,
        }));
        nextPackageList[f] = { ...nextPackageList[f], movMeta };

        const nameOfPackage = newFilesNames.find(
          (newNameFile) => newNameFile.name === currentPackage.name && newNameFile.originalName === currentPackage.originalName,
        )?.newName;

        const uploadAttempts = 10;
        for (let p = 0; p < uploadAttempts; p++) {
          try {
            availableToUpload = await postCreateVideoFeed({
              title: nameOfPackage ? nameOfPackage : currentPackage.name ? currentPackage.name : currentPackage.originalName,
              multi_quality: currentPackage.multi_quality,
              originalName: currentPackage.originalName,
              videos: videoSizeAndIdArray,
              upload_type: uploadType,
              folderId,
            });

            // If successful, exit the loop
            if (availableToUpload) {
              nextPackageList[f] = { ...nextPackageList[f], uploadError: '' };
              localStorage.setItem(
                keyStorage,
                JSON.stringify({
                  ...availableToUpload.data,
                  multi_quality: currentPackage.multi_quality,
                  fileListNames: currentPackage.fileListNames,
                  originalName: currentPackage.originalName,
                  size: currentPackage.size,
                  uploadedBytes: 0,
                }),
              );
              break;
            }
          } catch (error) {
            console.error(`Attempt ${p + 1} failed:`, error);
            // @ts-ignore
            if (error?.response?.data?.message) {
              // @ts-ignore
              const message = error.response.data.message;
              nextPackageList[f] = { ...nextPackageList[f], uploadError: message };
              showPersistentUploadWarning(message);
              break;
            }
          }

          // Wait for 5 seconds before retrying
          if (p < uploadAttempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, 5000));
          }
        }
      }

      if (availableToUpload) {
        uploadContentArr.push({
          ...availableToUpload.data,
          progress: availableToUpload.data?.overallProgress ? availableToUpload.data?.overallProgress : 0,
          status: uploadContentArr.length === 0 ? UploadingStatus.UPLOADING : UploadingStatus.ADDED,
          videofeedTitle: availableToUpload.data.title,
          originalName: currentPackage.originalName,
          videofeedId: availableToUpload.data.id,
          name: availableToUpload.data.title,
        });
      }

      if (availableToUpload?.overallProgress) {
        nextPackageList[f].overallProgress = availableToUpload?.overallProgress;
      }
    }

    const nextUploadingContentState = mergeUploadContentUploads(uploadContentArr, uploadingContentRef.current);
    uploadingContentRef.current = nextUploadingContentState;
    dispatch(updateUploadingContentState(nextUploadingContentState));
    const hasAppendPackages = packageList.some((pkg) => pkg.uploadMode === 'append');
    if (!uploadContentArr.length && !hasAppendPackages) {
      preparingUploadsRef.current = false;
      setLoading(false);
      setUploading(false);
      setCancelUpload(false);
      setRearrangeDisabled(false);
      return;
    }
    if (uploadContentArr.length) {
      // Trigger background refresh so drafts appear as soon as the first upload starts.
      dispatch(refetchFeeds());
    }
    const nextChildIndex = getNextUploadablePackageIndex(nextPackageList, 0);
    if (nextChildIndex === -1) {
      preparingUploadsRef.current = false;
      setPackageList(nextPackageList);
      packageListRef.current = nextPackageList;
      resetLoaders();
      setRearrangeDisabled(false);
      return;
    }
    const startedPackageList = startPackageUpload(nextPackageList, nextChildIndex);
    packageListRef.current = startedPackageList;
    setPackageList(startedPackageList);
    preparingUploadsRef.current = false;
    setRearrangeDisabled(false);
  };

  const resetLoaders = () => {
    setUploadedAll(true);
    setLoading(false);
    setUploading(false);
    setCancelUpload(false);
    dispatch(refetchFeeds());
  };

  const [uploadComplete, setUploadComplete] = useState(false);
  const getNextUploadablePackageIndex = (packages: TLocalPackage[], startIndex: number) => {
    return packages.findIndex((pkg, index) => index >= startIndex && !pkg.uploadError);
  };
  const startPackageUpload = (packages: TLocalPackage[], packageIndex: number) =>
    packages.map((pkg, index) => ({
      ...pkg,
      upload: index === packageIndex,
      uploadTrigger: index === packageIndex ? Date.now() : pkg.uploadTrigger,
    }));

  useEffect(() => {
    if (
      preparingUploadsRef.current ||
      !open ||
      !loading ||
      uploadedAll ||
      !packageList.length ||
      packageList.some((pkg) => pkg.upload)
    ) {
      return;
    }

    const nextChildIndex = getNextUploadablePackageIndex(packageList, 0);
    if (nextChildIndex === -1) {
      return;
    }

    const startedPackageList = startPackageUpload(packageList, nextChildIndex);
    packageListRef.current = startedPackageList;
    setPackageList(startedPackageList);
  }, [open, loading, uploadedAll, packageList, uploadingContentState]);

  const handleChildAsyncComplete = (childIndex: number, initial?: boolean) => {
    const arr = lodash.cloneDeep(packageListRef.current);
    const formattedArr = arr.map((elem) => {
      if (elem.originalName.endsWith('.MP4') || elem.name.endsWith('.MP4')) {
        let wrongName;
        if (elem.originalName.endsWith('.MP4')) {
          wrongName = elem.originalName;
        } else {
          wrongName = elem.name;
        }

        const base = wrongName.slice(0, -4);

        elem.name = base;
        elem.originalName = base;
      }

      return elem;
    });

    if (initial) {
      const nextPackageList = [...formattedArr];
      const nextChildIndex = getNextUploadablePackageIndex(nextPackageList, childIndex);
      if (nextChildIndex === -1) {
        resetLoaders();
        return;
      }
      const startedPackageList = startPackageUpload(nextPackageList, nextChildIndex);
      packageListRef.current = startedPackageList;
      setPackageList(startedPackageList);
      return;
    }

    const nextPackageList = [...formattedArr];
    const nextChildIndex = getNextUploadablePackageIndex(nextPackageList, childIndex + 1);

    if (nextChildIndex !== -1) {
      const startedPackageList = startPackageUpload(nextPackageList, nextChildIndex);
      packageListRef.current = startedPackageList;
      setPackageList(startedPackageList);
    } else {
      setUploadComplete(true);
      notification.showMessage('Uploading completed');
      resetLoaders();
    }
  };

  useEffect(() => {
    const uploadContentUploads = getUploadContentUploads(uploadingContentState);
    if (uploadContentUploads.length) {
      const uploadsInProgress = uploadContentUploads.filter((item: UploadContentState) =>
        [UploadingStatus.TRANSCODING, UploadingStatus.UPLOADING, UploadingStatus.ADDED].includes(item?.status),
      );
      if (uploading && !uploadsInProgress.length) {
        resetLoaders();
      }
    }
  }, [uploadingContentState]);

  const cancelAndClearLoading = async () => {
    setStopAndDeleteLoading(true);
    await abortActiveAwsUploads();
    const allUploads = uploadingContentRef.current;
    const uploads = getUploadContentUploads(allUploads);
    const additionalAngleUploads = getAdditionalAngleUploads(allUploads);
    const completedStatuses = new Set([UploadingStatus.TRANSCODING, UploadingStatus.FINISHED]);

    const deletable = uploads.filter((u) => u && !completedStatuses.has(u.status));
    const deletableNames = new Set(deletable.map((u) => u.originalName || (u as any).original_name).filter(Boolean));

    for (const upload of deletable) {
      try {
        await deleteThisVideofeed(upload.videofeedId || upload.id);
      } catch (err) {
        console.warn(err);
      }
    }

    if (deletableNames.size) {
      setPackageList((prev) => prev.filter((p) => !deletableNames.has(p.originalName)));
      setNewFileNames((prev) => prev.filter((p) => !deletableNames.has(p.originalName)));
      dispatch(updateUploadingContentState([...uploads.filter((u) => u && completedStatuses.has(u.status)), ...additionalAngleUploads]));
    }

    handleCancelUpload();
  };

  const cancelLoading = () => {
    handleCancelUpload();
  };

  const minimizeModal = () => {
    setMinimize(true);
  };

  const [cancelUpload, setCancelUpload] = useState(false);
  const handleCancelUpload = () => {
    void abortActiveAwsUploads();
    setCancelUpload(true);
    window.location.reload();
  };

  const hideModal = () => {
    if (uploadingRef.current || loadingRef.current) {
      minimizeModal();
    } else {
      switchModal();
      dispatch(updateUploadingContentState(getAdditionalAngleUploads(uploadingContentRef.current)));
    }
  };

  const [newFilesNames, setNewFileNames] = useState<NewFileNames[]>([]);
  const [savePrompt, setSavePropmt] = useState(false);
  const [renamedFeed, setRenamedFeed] = useState<{ id: number; title: string; i: number } | null>(null);
  const [disableInputs, setDisableInputs] = useState(false);
  const getUploadStateForPackage = (p: TLocalPackage, index: number) => {
    const uploadContentUploads = getUploadContentUploads(uploadingContentState);
    const nonAppendIndex = packageList.slice(0, index + 1).filter((pkg) => pkg?.uploadMode !== 'append').length - 1;
    return uploadContentUploads.find(
      (pkg, i) => pkg?.originalName === p.originalName && (i === index || i === nonAppendIndex),
    );
  };

  const onFileTitleChange = (e: any, p: TLocalPackage, index: number) => {
    const element = document.getElementById(`button_save_focus-${index}`);
    const input = document.getElementById(`special_input_selector_${index}`);
    if (element && input) {
      element.focus();
      setTimeout(() => {
        input.focus();
      });
    }
    if (loading && p.uploadMode !== 'append') {
      if (e.target?.value === p?.name) {
        setRenamedFeed(null);
        setDisableInputs(false);
        setRearrangeDisabled(false);
        setSavePropmt(false);
      } else {
        const pkgToRename = getUploadStateForPackage(p, index);
        if (pkgToRename && pkgToRename?.id) {
          setDisableInputs(true);
          setRearrangeDisabled(true);
          setSavePropmt(true);
          setRenamedFeed({ id: +pkgToRename.id, title: e.target.value, i: index });
        }
      }
    }
    const arr = [...newFilesNames];
    const newFileIndex = arr.findIndex(
      (fn) => fn.originalName === p.originalName && fn.movFiles.length === p.movFiles.length && fn.size === p.size,
    );
    if (newFileIndex !== -1) {
      arr[newFileIndex].newName = e.target.value;
    } else {
      arr.push({
        ...p,
        newName: e.target.value,
      });
    }
    setNewFileNames(arr);
  };

  const [renamingLoader, setRenamingLoader] = useState(false);
  const saveRenamingChanges = async (id: string, title: string, originalName: string) => {
    setRenamingLoader(true);
    try {
      await updateVideofeedTitle(id, title);
      const uploadPkgIndex = uploadingContentState.findIndex(
        (p) => !isAdditionalAngleUpload(p) && String(p.id || p.videofeedId) === String(id),
      );
      if (uploadPkgIndex !== -1) {
        const arr = lodash.cloneDeep(uploadingContentState);
        arr[uploadPkgIndex].name = title;
        // @ts-ignore
        arr[uploadPkgIndex].title = title;
        dispatch(updateUploadingContentState(arr));

        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key) {
            if (
              key.startsWith('=videofeedToResume=') &&
              localStorage &&
              localStorage.getItem(key) &&
              localStorage.getItem(key)?.includes(id)
            ) {
              const pkg = JSON.parse(localStorage.getItem(key)!);
              if (pkg && pkg?.id == id && (pkg?.original_name === originalName || pkg?.originalName === originalName)) {
                pkg.title = title;
                localStorage.setItem(key, JSON.stringify(pkg));
              }
            }
          }
        }
      }

      const pkgIndex = packageList.findIndex((p) => p.originalName === originalName);
      if (pkgIndex !== -1) {
        const arr = [...packageList];
        arr[pkgIndex].title = title;
        setPackageList(arr);
      }
      notification.showMessage(`${title} is saved`);
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      }
    } finally {
      setRenamedFeed(null);
      setRearrangeDisabled(false);
      setDisableInputs(false);
      setSavePropmt(false);
      setRenamingLoader(false);
    }
  };

  const cancelRenaming = (i: number, p: TLocalPackage) => {
    if (renamedFeed?.i === i || getUploadContentUploads(uploadingContentState)[i]) {
      const arr = [...newFilesNames];
      const newFileIndex = arr.findIndex((fn) => fn.originalName === p.originalName && fn.movFiles.length === p.movFiles.length);
      if (newFileIndex !== -1) {
        arr[newFileIndex].newName = p.name || p.originalName;
      } else {
        arr.push({
          ...p,
          newName: p.name || p.originalName,
        });
      }
      setNewFileNames(arr);
      setRenamedFeed(null);
      setRearrangeDisabled(false);
      setDisableInputs(false);
      setSavePropmt(false);
      setRenamingLoader(false);
    }
  };

  useEffect(() => {
    if (packageList?.length) {
      const lengthOfVideoPackages = packageList.filter((p) => p.singleMov && p.movFiles.length > 1 && !p?.title).length;
      const lengthInFilesNames = newFilesNames.filter((f) => f.singleMov && f.movFiles.length > 1 && f.newName.trim().length > 0).length;
      if (lengthOfVideoPackages !== lengthInFilesNames) {
        if (!loading) {
          setUploadEnabled(false);
        }
      } else {
        setUploadEnabled(true);
      }
    } else {
      setUploadEnabled(true);
    }
  }, [packageList, newFilesNames]);

  const setMultiquality = (b: boolean, i: number) => {
    const arr = [...packageList];
    arr[i] = { ...arr[i], multi_quality: b };
    setPackageList((prev) => arr);
  };

  const onQualityCheckBoxChange = (index: number, q?: boolean) => {
    if (typeof q !== 'undefined') {
      setMultiquality(q, index);
    } else {
      setMultiquality(!packageList[index].multi_quality, index);
    }
  };

  const [showChangedPackageModal, setShowChangedPackageModal] = useState(false);
  const [pendingChangedPackage, setPendingChangedPackage] = useState<TLocalPackage | null>(null);
  const [resumableToReplace, setResumableToReplace] = useState<ExtendedVIDEOFEED | null>(null);

  const clearChangedPackageModalState = () => {
    setShowChangedPackageModal(false);
    setPendingChangedPackage(null);
    setResumableToReplace(null);
  };

  const confirmReplaceChangedPackage = async () => {
    if (!pendingChangedPackage || !resumableToReplace) {
      clearChangedPackageModalState();
      return;
    }
    try {
      await deleteThisVideofeed(resumableToReplace.id);
      setPackageList((prev) => [...prev, pendingChangedPackage]);
      clearChangedPackageModalState();
    } catch {
      notification.showMessage('Failed to delete previous upload. Please try again.');
    }
  };

  const renderSizeWithBytesAndMb = (size?: number) => {
    const normalized = Number(size || 0);
    return `${normalized} bytes (${bytesToMB(normalized)} MB)`;
  };

  const handleNewPackages = (newPackages: TLocalPackage[]) => {
    if (+myOrganization?.hours_limit_used >= +myOrganization?.hours_limit) {
      showPersistentUploadWarning('Hours limit reached');
      return;
    }
    let alreadyExist = false;
    const filteredPackages = newPackages.filter((newPackage) => {
      const alreadyExists = packageList.some(
        (packageItem) => packageItem.originalName === newPackage.originalName && packageItem.size === newPackage.size,
      );
      const changedResumable = resumablePackages.find(
        (resumablePackage) =>
          resumablePackage.originalName === newPackage.originalName && Number(resumablePackage.size) !== Number(newPackage.size),
      );
      if (alreadyExists) {
        alreadyExist = true;
        notification.showMessage(`You already have this package/video ${newPackage.originalName} in the upload queue`);
      }
      if (changedResumable) {
        setPendingChangedPackage(newPackage);
        setResumableToReplace(changedResumable);
        setShowChangedPackageModal(true);
        return false;
      }
      return !alreadyExists;
    });

    const formattedArr = filteredPackages.map((elem) => {
      if (elem.originalName.endsWith('.MP4') || elem.name.endsWith('.MP4')) {
        let wrongName;
        if (elem.originalName.endsWith('.MP4')) {
          wrongName = elem.originalName;
        } else {
          wrongName = elem.name;
        }

        const base = wrongName.slice(0, -4);

        elem.name = base;
        elem.originalName = base;
      }

      return elem;
    });

    if (alreadyExist) {
      setDisableDropMore(false);
    }

    setPackageList((currentPackages) => {
      const nextPackages = [...currentPackages, ...formattedArr];
      packageListRef.current = nextPackages;
      return nextPackages;
    });
    setTimeout(() => {
      setRearrangeDisabled(false);
    }, 1000);
  };

  const [resumablePackages, setResumablePackages] = useState<ExtendedVIDEOFEED[]>([]);

  useEffect(() => {
    const videoFeedToResumeValues = [];
    const folderId = findSearchValue('folderId');
    if (folderId) {
      for (let i = 0; i < localStorage.length; i++) {
        let key = localStorage.key(i);
        const cachedPkg = key ? uploadingFiles.find((uF) => uF.keyStorage === key) : undefined;
        if (cachedPkg && cachedPkg?.data && cachedPkg.data.uploadMode !== 'append') {
          const pkgIndex = packageList.findIndex(
            (p) =>
              cachedPkg?.data &&
              p.originalName === cachedPkg?.data?.originalName &&
              p.size === cachedPkg.data.size &&
              p.movFiles.length === cachedPkg.data.videos.length,
          );
          if (pkgIndex !== -1) {
            const newPkgList = [...packageList];
            newPkgList[pkgIndex].multi_quality = cachedPkg.data.multi_quality!;
            newPkgList[pkgIndex].disableQuality = true;
            newPkgList[pkgIndex].title = cachedPkg?.data?.title;
            if (cachedPkg.data.uploadedBytes) {
              newPkgList[pkgIndex].uploadedBytes = cachedPkg.data.uploadedBytes;
            }
            setPackageList(newPkgList);
          }
        }

        if (
          key &&
          key?.startsWith('=videofeedToResume=') &&
          key.includes(`=${folderId}::`) &&
          !uploadingFiles.find((uF) => uF.keyStorage === key)
        ) {
          let value = localStorage.getItem(key);
          if (value?.length) {
            const parsedValue = JSON.parse(value);
            if (parsedValue?.uploadMode !== 'append') {
              videoFeedToResumeValues.push(parsedValue);
            }
          }
        }
      }
    }
    setResumablePackages(videoFeedToResumeValues);
  }, [open, refetchFeedList, uploadingFiles]);

  const [deleting, setDeleting] = useState(false);
  const refreshMyOrganization = async () => {
    try {
      const resp = await getUserOrganization();
      dispatch(saveMyOrganization(resp.data));
    } catch (err) {
      console.error(err);
    }
  };
  const onClearingAllResumablePackages = async () => {
    const folderId = findSearchValue('folderId');
    const folderPrefix = `=videofeedToResume=${folderId}::`;
    setDeleting(true);
    setLoading(true);
    try {
      for (let i = 0; i < resumablePackages.length; i++) {
        const searchString = String(resumablePackages[i].id);
        for (let j = localStorage.length - 1; j >= 0; j--) {
          const key = localStorage.key(j);
          if (!key || !key.startsWith(folderPrefix)) continue;

          const raw = localStorage.getItem(key);
          if (!raw) continue;

          try {
            const pkg = JSON.parse(raw);
            if (String(pkg?.id || '') === searchString) {
              localStorage.removeItem(key);
            }
          } catch {
            // ignore malformed resume payloads
          }
        }
        await deleteVideofeed({
          videofeedId: resumablePackages[i].id,
          confirm: true,
        });
      }
      await refreshMyOrganization();
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key?.startsWith(folderPrefix)) {
          localStorage.removeItem(key);
        }
      }
    } finally {
      setDeleting(false);
    }
    setResumablePackages([]);
    dispatch(refetchFeeds());
    setLoading(false);
  };

  const deleteThisVideofeed = async (id: string) => {
    const folderId = findSearchValue('folderId');
    const folderPrefix = `=videofeedToResume=${folderId}::`;
    setDeleting(true);
    const match = getUploadContentUploads(uploadingContentRef.current).find(
      (item) => String(item?.mergeSessionId || item?.videofeedId || item?.id || '') === String(id),
    );

    if (match?.uploadMode === 'append' && String(match?.mergeSessionId || '') === String(id)) {
      let cancelError: unknown = null;
      try {
        await postCancelMergeSession(id);
      } catch (error) {
        cancelError = error;
      }
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (!key || !key.startsWith(folderPrefix)) continue;

          const raw = localStorage.getItem(key);
          if (!raw) continue;

          try {
            const pkg = JSON.parse(raw);
            if (String(pkg?.id || '') === String(id)) {
              localStorage.removeItem(key);
            }
          } catch {
            // ignore malformed resume payloads
          }
        }
        const arr = resumablePackages.filter((v) => v.id !== id);
        setResumablePackages(arr);
        await refreshMyOrganization();
        dispatch(refetchFeeds());
        if (cancelError) {
          console.warn(cancelError);
        }
      } finally {
        setDeleting(false);
      }
      return;
    }

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(folderPrefix)) continue;

      const raw = localStorage.getItem(key);
      if (!raw) continue;

      try {
        const pkg = JSON.parse(raw);
        if (String(pkg?.id || '') === String(id)) {
          localStorage.removeItem(key);
        }
      } catch {
        // ignore malformed resume payloads
      }
    }
    const arr = resumablePackages.filter((v) => v.id !== id);
    setResumablePackages(arr);
    await deleteVideofeed({ videofeedId: id, confirm: true });
    await refreshMyOrganization();
    dispatch(refetchFeeds());
    setDeleting(false);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (!packageList[result.source.index] || !packageList[result.destination.index]) return;
    if (packageList[result.destination.index].upload) return;
    const reorderedPackageList = Array.from(packageList);
    const [reorderedItem] = reorderedPackageList.splice(result.source.index, 1);
    if (!reorderedItem) return;
    reorderedPackageList.splice(result.destination.index, 0, reorderedItem);

    const currentUploads = lodash.cloneDeep(uploadingContentRef.current).filter(Boolean);
    const additionalAngleUploads = getAdditionalAngleUploads(currentUploads);
    const uploadArr = getUploadContentUploads(currentUploads);
    if (uploadArr.length) {
      const reorderedUploadArr = reorderedPackageList.reduce<UploadContentState[]>((acc, pkg) => {
        const match = uploadArr.find((upload) => !acc.includes(upload) && uploadMatchesPackage(upload, pkg));
        if (match) {
          acc.push(match);
        }
        return acc;
      }, []);
      const remainingUploads = uploadArr.filter((upload) => !reorderedUploadArr.includes(upload));
      dispatch(updateUploadingContentState([...reorderedUploadArr, ...remainingUploads, ...additionalAngleUploads]));
    }

    // on drag
    packageListRef.current = reorderedPackageList;
    setPackageList(reorderedPackageList);
  };

  const onPackageRemove = async (originalName: string, packageIndex?: number) => {
    const packages = lodash.cloneDeep(packageListRef.current);
    const allUploads = lodash.cloneDeep(uploadingContentRef.current);
    const uploads = getUploadContentUploads(allUploads);
    const additionalAngleUploads = getAdditionalAngleUploads(allUploads);
    const packageToRemove = typeof packageIndex === 'number' ? packages[packageIndex] : undefined;
    let match: UploadContentState | undefined;

    if (!packageToRemove?.uploadError) {
      // @ts-ignore
      match = packageToRemove
        ? uploads.find((p) => uploadMatchesPackage(p, packageToRemove))
        : uploads.find((p) => p?.originalName === originalName || (p as any)?.original_name === originalName);
    }

    if (match?.videofeedId || match?.id) {
      try {
        await deleteThisVideofeed(match.videofeedId || match.id);
      } catch (err) {
        console.warn(err);
      }
    }

    const filteredPackages =
      typeof packageIndex === 'number'
        ? packages.filter((_, index) => index !== packageIndex)
        : packages.filter((p) => p?.originalName !== originalName);
    const filteredNames = lodash
      .cloneDeep(newFilesNames)
      .filter((name) => filteredPackages.some((p) => p?.originalName === name?.originalName));
    // @ts-ignore
    const filteredUploads = match
      ? uploads.filter((p) => p !== match)
      : uploads.filter((p) => p?.originalName !== originalName && (p as any)?.original_name !== originalName);

    packageListRef.current = filteredPackages;
    setPackageList(filteredPackages);
    setNewFileNames(filteredNames);
    setTimeout(() => {
      dispatch(updateUploadingContentState([...filteredUploads, ...additionalAngleUploads]));
    }, 2000);
  };

  useEffect(() => {
    if (pkgToDelete?.original_name) {
      onPackageRemove(pkgToDelete.original_name);
    }
  }, [pkgToDelete]);

  const setPackageUploadError = (packageIndex: number, message: string) => {
    setPackageList((prev) => {
      const next = [...prev];
      if (next[packageIndex]) {
        next[packageIndex] = { ...next[packageIndex], uploadError: message };
      }
      packageListRef.current = next;
      return next;
    });
  };

  const renderPackages = useMemo(() => {
    const currentFolderId = folderIdToUpload || (findSearchValue('folderId') as string) || '';

    return (
      packageList.length > 0 && (
        <div ref={packageListContainerRef} className={cx(styles.awsFileList, 'CustomScrollbar')}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {packageList.map((p, i) => (
                    <Draggable
                      key={`${p.name}_${p.size}`}
                      draggableId={`${p.name}_${p.size}`}
                      index={i}
                      isDragDisabled={p.upload || rearrangeDisabled}
                    >
                      {(provided: any, snapshot) => {
                        if (snapshot.isDragging) {
                          provided.draggableProps.style.left = provided.draggableProps.style.offsetLeft;
                        }
                        return (
                          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            <div
                              key={`${p.name}_${i}`}
                              className={cx(styles.awsFile, p.uploadMode === 'append' && styles.appendAwsFile)}
                              style={{ position: 'relative', marginRight: '5px' }}
                            >
                              {p.uploadMode === 'append' && <div className={styles.appendBadge}>Append video</div>}
                              {(newFilesNames.find(
                                (f) =>
                                  f?.movFiles.length === p?.movFiles.length && f?.originalName === p?.originalName && p.size === f.size,
                              )?.newName ||
                                p?.title) && (
                                <div className={styles.customTitle}>
                                  {' '}
                                  Package title:{' '}
                                  {newFilesNames.find(
                                    (f) =>
                                      f?.movFiles.length === p?.movFiles.length && f?.originalName === p?.originalName && p.size === f.size,
                                  )?.newName
                                    ? newFilesNames.find(
                                        (f) =>
                                          f?.movFiles.length === p?.movFiles.length &&
                                          f?.originalName === p?.originalName &&
                                          p.size === f.size,
                                      )?.newName
                                    : p.title}{' '}
                                </div>
                              )}
                              <AwsFile
                                tempId={`${p.name}_${p.size}`}
                                {...p}
                                name={
                                  newFilesNames.find(
                                    (f) =>
                                      f?.movFiles.length === p?.movFiles.length && f?.originalName === p?.originalName && p.size === f.size,
                                  )?.newName || p.title || p.name || p.originalName
                                }
                                nextCallBack={handleChildAsyncComplete}
                                index={i}
                                cancel={cancelUpload}
                                hideModal={hideModal}
                                loading={loading}
                                onQualityCheckBoxChange={onQualityCheckBoxChange}
                                setRearrangeDisabled={setRearrangeDisabled}
                                setDisableDropMore={setDisableDropMore}
                                createOnce={createOnce}
                                folderId={currentFolderId}
                                stableKey={stableKey}
                                setPackageUploadError={(message) => setPackageUploadError(i, message)}
                              />
                              {myOrganization.allowedVideoQualities && (
                                <Tooltip
                                  title={
                                    p?.multi_quality
                                      ? 'Additional video qualities selected'
                                      : 'Add additional video qualities to the original video, which will increase the storage space usage'
                                  }
                                >
                                  <div className={styles.toggle}>
                                    <span>Additional qualities</span>
                                    {p.localStorageKey}
                                    <div className={styles.checkboxWrapper30}>
                                      <span className={styles.checkbox}>
                                        <input
                                          disabled={loading || !!status.length || uploadedAll || p?.disableQuality}
                                          onChange={() => onQualityCheckBoxChange(i)}
                                          type="checkbox"
                                          className={styles.checkbox}
                                          checked={p.multi_quality}
                                          value={`${p.multi_quality}`}
                                        />
                                        <svg>
                                          <use xlinkHref="#checkbox-30" className={styles.checkbox}></use>
                                        </svg>
                                      </span>
                                      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                                        <symbol id="checkbox-30" viewBox="0 0 22 22">
                                          <path
                                            fill="none"
                                            stroke="currentColor"
                                            d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                                          />
                                        </symbol>
                                      </svg>
                                    </div>
                                  </div>
                                </Tooltip>
                              )}
                              {p.singleMov && !p.upload && (
                                <div style={{ display: 'flex' }}>
                                  <input
                                    name="standalone-title"
                                    onChange={(e) => onFileTitleChange(e, p, i)}
                                    id={`special_input_selector_${i}`}
                                    required={p.movFiles.length > 1}
                                    className={styles.uploadContentTitleField}
                                    placeholder={p.movFiles.length > 1 ? 'Enter The Title' : p.name}
                                    disabled={p.upload || !!status.length || uploadedAll || (disableInputs && renamedFeed?.i !== i)}
                                    value={
                                      newFilesNames.find(
                                        (f) =>
                                          f?.movFiles.length === p?.movFiles.length &&
                                          f?.originalName === p?.originalName &&
                                          f.size === p.size,
                                      )?.newName
                                    }
                                  />
                                </div>
                              )}
                              {renamedFeed?.i === i && savePrompt && renamedFeed?.id && renamedFeed?.title && (
                                <>
                                  <ContainedButton
                                    style={{ marginBottom: '20px', marginRight: '16px' }}
                                    kind="red"
                                    onClick={() => cancelRenaming(i, p)}
                                    type="button"
                                  >
                                    {' '}
                                    Cancel changes{' '}
                                  </ContainedButton>
                                  <ContainedButton
                                    onClick={() =>
                                      saveRenamingChanges(`${renamedFeed.id}`, renamedFeed.title, p.originalName)
                                    }
                                    type="button"
                                    id={`button_save_focus-${i}`}
                                    style={{ outline: 'none', boxShadow: 'none' }}
                                  >
                                    {' '}
                                    Save changes{' '}
                                  </ContainedButton>
                                </>
                              )}
                              <Divider style={{ backgroundColor: '#686161', marginBottom: '5px' }} />
                              {(!loading ||
                                !!p.uploadError ||
                                // @ts-ignore
                                getUploadContentUploads(uploadingContentState).find(
                                  // @ts-ignore
                                  (v) => v?.originalName === p?.originalName,
                                )?.status === UploadingStatus.ADDED) &&
                                !uploadedAll && (
                                  <div onClick={() => onPackageRemove(p.originalName, i)} className={styles.removeFromUploadQueueIcon}>
                                    <Tooltip title={'Remove from queue'}>
                                      <Close />
                                    </Tooltip>
                                  </div>
                                )}
                            </div>
                          </div>
                        );
                      }}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )
    );
  }, [packageList, uploadingContentState, uploading, loading, uploadedAll, newFilesNames, rearrangeDisabled, renamedFeed, folderIdToUpload]);

  const renderUploadedSize = useMemo(() => {
    let uploaded = 0;
    let totalSizeOfPackages = 0;
    uploaded = bytesToMB(arrayOfCurrentUploads.reduce((a, b) => (a += b?.uploadedBytes ? b.uploadedBytes : 0), 0));
    totalSizeOfPackages = bytesToMB(totalSize);

    return (
      <div style={{ fontSize: '11.5px' }}>
        {uploaded}
        MB / {totalSizeOfPackages}
        MB
      </div>
    );
  }, [arrayOfCurrentUploads, totalSize]);

  useHotkeys('Enter', () => {
    if (!open || loading || deleting || stopAndDeleteLoading) return;

    if (uploadedAll) {
      doneButtonRef?.current?.click();
    } else {
      uploadButtonRef?.current?.click();
    }
  });

  return (
    <Modal
      className={cx(showMinimizedModal ? styles.hide : '')}
      open={open}
      onClose={loading || stopAndDeleteLoading ? () => {} : switchModal}
      loading={loading || deleting}
    >
      <FullPageLoader loading={stopAndDeleteLoading} />
      <div className={styles.modalContainer}>
        <div className={styles.modalTitle}>
          {loading ? `Uploading ${packageList.length} item(s)` : 'Upload Content'}
          {renamingLoader && <LinearProgress />}
          <br />
          {!loading && resumablePackages.length > 0 && (
            <div className={styles.resumePackageContainer}>
              <div className={styles.resumeTitle}>
                <span>Uploading of this packages can be resumed:</span>{' '}
                {packageList.length === 0 && (
                  <Tooltip title={'clear all'}>
                    <TrashButton onClick={!loading ? onClearingAllResumablePackages : () => {}} />
                  </Tooltip>
                )}
              </div>
              <div className={styles.resumePackageList}>
                {resumablePackages.map((v) => (
                  <div key={`${v.id}_resume`} className={styles.resume}>
                    <div className={styles.resumePackage} style={{ width: '100%', maxWidth: '320px', textWrap: 'wrap', wordBreak: 'break-word' }}>
                      Title: {v.title}{' '}
                      {v?.fileListNames?.length > 0 ? (
                        <div className={styles.originalPackage}>
                          Original file names:{' '}
                          {v.fileListNames.map((fileNameString) => (
                            <div
                              style={{ margin: '8px 0', width: '100%', maxWidth: '320px', textWrap: 'wrap', wordBreak: 'break-word' }}
                              key={fileNameString}
                            >
                              {fileNameString}
                            </div>
                          ))}
                        </div>
                      ) : (
                        v.originalName?.length > 0 &&
                        v.originalName !== v.title && <div className={styles.originalPackage}>Original file name: {v.originalName}</div>
                      )}
                      {/* @ts-ignore */}
                      <div className={styles.originalPackage} style={{ fontWeight: 300, marginTop: '6px' }}>
                        {' '}
                        Uploaded: {v?.overallProgress ?? 0}%
                      </div>
                      <div className={styles.originalPackage} style={{ fontWeight: 300, marginTop: '6px' }}>
                        {' '}
                        Size: {bytesToMB(v.size)} MB
                      </div>
                      <div className={styles.originalPackage} style={{ fontWeight: 300, marginTop: '6px' }}>
                        {' '}
                        {v.multi_quality ? '(+ additional qualities)' : ''}{' '}
                      </div>
                    </div>
                    <div className={styles.deleteIcon}>
                      <Tooltip title={'delete this package'}>
                        <Close
                          style={{ fontSize: '19px', height: '12px' }}
                          onClick={!deleting ? () => deleteThisVideofeed(v.id) : () => {}}
                        />
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Close className={styles.iconButtonClose} onClick={switchModal} />
          <Minimize className={styles.iconButtonMinimize} onClick={minimizeModal} />
        </div>
        {!uploadedAll && (loading || arrayOfCurrentUploads.reduce((a, b) => (a += b?.uploadedBytes ? b.uploadedBytes : 0), 0) > 0) && (
          <Tooltip
            title={`${bytesToMB(arrayOfCurrentUploads.reduce((a, b) => (a += b?.uploadedBytes ? b.uploadedBytes : 0), 0))} MB / ${bytesToMB(
              totalSize,
            )} MB`}
          >
            <div className={styles.timeRemain}>
              <span>{renderUploadedSize}</span>
              <ProgressBar
                maxValue={totalSize}
                value={arrayOfCurrentUploads.reduce((a, b) => (a += b?.uploadedBytes ? b.uploadedBytes : 0), 0)}
              />
            </div>
          </Tooltip>
        )}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div className={styles.fileUploader}>
            {renderPackages}

            {showFileInput && !disableDropMore && !savePrompt && (
              <FileUpload
                disable={!uploadEnabled || uploadComplete}
                setPackageList={handleNewPackages}
                small={packageList.length > 0 ? true : false}
                setRearrangeDisabled={setRearrangeDisabled}
              />
            )}
          </div>
          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {uploadedAll ? (
              <ContainedButton disabled onClick={hideModal} type="button">
                {loading ? 'Uploading...' : 'Close'}
              </ContainedButton>
            ) : (
              <>
                <ContainedButton disabled={!packageList.length || loading || !uploadEnabled} type="submit">
                  <div ref={uploadButtonRef}>{loading ? 'Uploading...' : 'Upload file'}</div>
                </ContainedButton>
                <OutlinedButton kind="default" className={styles.outlinedBtn} onClick={switchModal}>
                  Cancel
                </OutlinedButton>
              </>
            )}

            {uploadedAll && (
              <ContainedButton onClick={hideModal} type="button">
                <div ref={doneButtonRef}>Done</div>
              </ContainedButton>
            )}
          </form>
        </div>
      </div>
      <CancelProcessModal
        open={showCancelUploadModal}
        switchModal={switchCancelUploadModal}
        cancelCallBack={cancelLoading}
        cancelUpload={cancelUpload || stopAndDeleteLoading}
        cancelAndClearLoading={cancelAndClearLoading}
      />
      <Modal open={showChangedPackageModal} onClose={clearChangedPackageModalState}>
        <div style={{ padding: '20px', width: '520px', maxWidth: 'calc(100vw - 40px)', boxSizing: 'border-box', overflowX: 'hidden' }}>
          <div style={{ fontSize: '20px', fontWeight: 600, marginBottom: '10px' }}>Package has changed</div>
          <div style={{ fontSize: '14px', marginBottom: '18px' }}>
            This package will be treated as a new upload because its content changed.
          </div>
          <div style={{ fontSize: '14px', marginBottom: '6px' }}>
            Previous resumable size: {renderSizeWithBytesAndMb(resumableToReplace?.size)}
          </div>
          <div style={{ fontSize: '14px', marginBottom: '18px' }}>
            New upload size: {renderSizeWithBytesAndMb(pendingChangedPackage?.size)}
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <ContainedButton kind="purple" onClick={confirmReplaceChangedPackage} disabled={deleting}>
              Delete previous upload and start new
            </ContainedButton>
            <OutlinedButton kind="default" onClick={clearChangedPackageModalState} disabled={deleting}>
              Cancel
            </OutlinedButton>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default UploadContentModal;
